import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { gql, useQuery } from '@apollo/client';
import React from 'react';
import { Alert, Button, Checkbox, InputNumber, Select, Space, Table, Typography } from 'antd';

export type CustomFieldsImportMappingRow = {
  id: string;
  columnIndex?: number;
  customFieldCode?: string;
  addNewTextValue: boolean;
  addNewOption: boolean;
  replace: boolean;
};

type CustomFieldOption = {
  code: string;
  heading: string;
  fieldTypeEnum: string;
  allowMultiple?: boolean | null;
  allowUserCreate?: boolean | null;
};

type CustomFieldsImportMappingProps = {
  rows: CustomFieldsImportMappingRow[];
  setRows: React.Dispatch<React.SetStateAction<CustomFieldsImportMappingRow[]>>;
  fixedColumns: FixedColumn[];
  firstCustomFieldColumn: number;
};

type FixedColumn = {
  id: string | number;
  lineDataIndex: number;
  field: {
    code: string;
    heading?: string | null;
    note?: string | null;
  };
};

const CustomFieldsImportMapping: React.FC<CustomFieldsImportMappingProps> = ({
  rows,
  setRows,
  fixedColumns,
  firstCustomFieldColumn,
}) => {
  const { data, loading } = useQuery<{
    customFields: {
      nodes: CustomFieldOption[];
    };
  }>(CUSTOM_FIELDS_QUERY);

  const customFields = data?.customFields.nodes ?? [];

  const updateRow = (
    id: string,
    patch: Partial<CustomFieldsImportMappingRow>
  ) => {
    setRows(current => current.map(row => {
      if (row.id !== id) return row;
      const next = { ...row, ...patch };
      if (next.addNewTextValue) {
        next.addNewOption = false;
        next.replace = patch.replace ?? next.replace;
      }
      return next;
    }));
  };

  const addRow = () => {
    const nextColumnIndex = rows
      .map(row => row.columnIndex)
      .filter((value): value is number => value !== undefined)
      .reduce((max, value) => Math.max(max, value), firstCustomFieldColumn - 1) + 1;

    setRows(current => [
      ...current,
      {
        id: `${Date.now()}-${current.length}`,
        columnIndex: nextColumnIndex,
        addNewTextValue: false,
        addNewOption: false,
        replace: false,
      },
    ]);
  };

  const removeRow = (id: string) => {
    setRows(current => current.filter(row => row.id !== id));
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={4}>Custom fields import mapping</Typography.Title>
      <Alert
        type="info"
        showIcon
        message="Fixed columns"
        description={[
          'These columns are read by the custom import processor. ',
          'Add rows below for extra CSV columns that should be imported as person custom fields.',
        ].join('')}
      />
      <Table
        size="small"
        pagination={false}
        rowKey="id"
        dataSource={fixedColumns}
        columns={[
          {
            title: 'Column index',
            dataIndex: 'lineDataIndex',
            width: 140,
          },
          {
            title: 'Column name',
            render: (_, row: FixedColumn) => row.field.heading ?? row.field.code,
            width: 220,
          },
          {
            title: 'Description',
            dataIndex: ['field', 'note'],
          },
        ]}
      />
      <Table<CustomFieldsImportMappingRow>
        size="small"
        pagination={false}
        rowKey="id"
        dataSource={rows}
        locale={{ emptyText: 'No custom field columns selected' }}
        columns={[
          {
            title: 'CSV column',
            dataIndex: 'columnIndex',
            width: 130,
            render: (_, row) => (
              <InputNumber
                min={firstCustomFieldColumn}
                precision={0}
                value={row.columnIndex}
                onChange={value => updateRow(row.id, { columnIndex: value ?? undefined })}
                style={{ width: '100%' }}
              />
            ),
          },
          {
            title: 'Custom field',
            dataIndex: 'customFieldCode',
            render: (_, row) => (
              <Select
                showSearch
                loading={loading}
                value={row.customFieldCode}
                placeholder="Select custom field"
                optionFilterProp="label"
                onChange={customFieldCode => updateRow(row.id, { customFieldCode })}
                style={{ width: '100%' }}
                options={customFields.map(field => ({
                  value: field.code,
                  label: `${field.heading} (${field.code})`,
                }))}
              />
            ),
          },
          {
            title: 'New text value',
            dataIndex: 'addNewTextValue',
            width: 150,
            render: (_, row) => (
              <Checkbox
                checked={row.addNewTextValue}
                onChange={event => updateRow(row.id, {
                  addNewTextValue: event.target.checked,
                  addNewOption: event.target.checked ? false : row.addNewOption,
                  replace: event.target.checked ? true : row.replace,
                })}
              />
            ),
          },
          {
            title: 'New option',
            dataIndex: 'addNewOption',
            width: 120,
            render: (_, row) => (
              <Checkbox
                checked={row.addNewOption}
                disabled={row.addNewTextValue}
                onChange={event => updateRow(row.id, { addNewOption: event.target.checked })}
              />
            ),
          },
          {
            title: 'Replace',
            dataIndex: 'replace',
            width: 100,
            render: (_, row) => (
              <Checkbox
                checked={row.replace}
                onChange={event => updateRow(row.id, { replace: event.target.checked })}
              />
            ),
          },
          {
            title: '',
            key: 'action',
            width: 70,
            render: (_, row) => (
              <Button
                type="text"
                icon={<DeleteOutlined />}
                onClick={() => removeRow(row.id)}
              />
            ),
          },
        ]}
      />
      <Button icon={<PlusOutlined />} onClick={addRow}>
        Add custom field column
      </Button>
    </Space>
  );
};

const CUSTOM_FIELDS_QUERY = gql`
  query PersonCustomFieldsForImport {
    customFields(criteria: { entityType: PERSON, isActive: true }) {
      hash
      nodes {
        code
        heading
        fieldTypeEnum
        allowMultiple
        allowUserCreate
      }
    }
  }
`;

export default CustomFieldsImportMapping;
